import { Fragment, useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router'
import { AREA_PAGES } from '../areaPages'
import { Card, Tag } from '../../ui'
import { getFindings, getOverview, getResolvedFindings } from '../../lib/api'
import type {
  FindingSummary, NotLicensedControls, ResolvedFindings, ScanFreshness, UnreadControl,
} from '../../lib/api'
import type { Severity } from '../../ui'
import { CoverageLedger } from './CoverageLedger'
import { canRescan, Freshness, RescanButton } from './Freshness'
import { FindingScope } from './FindingScope'
import {
  controlCount, controls as controlsWord, noLicenceFor, notAssessedAreasSentence, notLicensedIn,
  notLicensedOf, serviceNames, wholeAreaNotLicensed,
} from './notLicensed'
import { SecureScoreGauge } from './SecureScoreGauge'
import { agrees, personOf, SESSION_CHANGED, SESSION_UNKNOWN, useSession } from './session'
import type { SessionState } from './session'
import { partlyReadAreas, partlyReadNote, unconfirmedAreas } from './partlyRead'
import { isWaiting, NO_DEVICES_FINDING, sectionsOf } from './unreadReasons'
import type { UnreadSection } from './unreadReasons'
import { describeFailure, useApi } from './useApi'
import type { Load } from './useApi'
import './live.css'

/** The four ranked levels, worst first. Informational is not a rank — see the row below. */
const RANKED = ['Critical', 'High', 'Medium', 'Low'] as const

/** How many titles a severity card carries under its count, and how many rows "fix these first" is. */
const TITLES_PER_CARD = 3
const FIX_FIRST_ROWS = 5

/**
 * The dashboard, from the real API.
 *
 * Every number on this page came from one tenant's database rows under that tenant's capability.
 * Nothing is fixture data and nothing is computed client-side from a sample — this is the page a
 * customer screenshots for their board, and a plausible-looking number that is not theirs would be
 * the most damaging thing this product could produce.
 *
 * Which is also why freshness renders first and unconditionally. Counts are meaningless without
 * knowing whether the scan behind them finished.
 *
 * The layout is the approved design: two headline panels — what we look at, and what we found —
 * then a severity card per level carrying its own findings, then what to do first beside what has
 * already been done. NOTHING ON IT IS A SCORE OF OURS. Not a percentage, not a grade, not a figure
 * out of a hundred; see the note at the top of CoverageLedger.tsx and docs/launch-plan.md. The
 * design has none either, and the two headline panels are what stands where a competitor puts a
 * donut. The one percentage on the page is Microsoft's Secure Score (owner, 2026-09-25), which sits
 * BELOW those two rather than among them and says in words whose number it is -- see
 * SecureScoreGauge.tsx.
 */
export function LiveDashboard({
  onOpen,
}: {
  /**
   * Opens one finding. Optional, because the dashboard is legible without it — but every title on
   * this page names a real row, and a list of problems that cannot be opened is a dead end.
   */
  onOpen?: (finding: FindingSummary) => void
}) {
  const session = useSession()

  // Bumped when a scan is queued, for one reason: to make useApi ask again. See the note beside
  // Freshness below.
  const [queued, setQueued] = useState(0)

  // While a scan is moving, keep asking. Without this the card was rendered once on mount and then
  // left there, so an assessment sat at "0 of 2" until the person reloaded the page -- and a scan
  // that had finished thirty seconds ago looked identical to one that had stalled.
  //
  // keepPrevious on all three reads: "Assess now" bumps `queued`, and without it the whole page --
  // header included -- dropped to "Loading…", so the "Queued" confirmation beside the button was
  // unmounted before anyone read it and keyboard focus fell to the page body.
  const overview = useApi(
    () => getOverview(), [queued],
    (data) => data.freshness.status === 'running' || data.freshness.scanPending,
    true)

  /*
    ONE worst-first read, powering three things: the titles inside each severity card, the ordered
    "fix these first" list, and the worst level present in each area.

    Deliberately not five reads. The counts on this page are the server's, from /api/overview,
    which is a consistent snapshot; this read only supplies TITLES for numbers that came from
    there. Asking per severity would be five requests whose answers can disagree with each other
    and with the counts above them, and a card reading "21" over three titles from a different
    moment is a page that contradicts itself.

    pageSize is the server's maximum. A hundred worst-first rows is enough to reach the lower
    severities on a real tenant; where it is not, a card shows its count with no titles under it
    rather than an invented "none".
  */
  const worst = useApi(
    () => getFindings({ status: 'active', sort: 'severity', pageSize: 100 }), [queued],
    (data) => data.freshness.status === 'running' || data.freshness.scanPending,
    true)

  // Its own read, and allowed to fail on its own: sharing the dashboard's main read would take the
  // whole page down over a panel that is meant to degrade quietly.
  //
  // Not polled, but read again once the overview first answers, and after that whenever its poll
  // reports a different latest scan or the same scan in a new state. A scan resolves findings in
  // the same transaction that closes it, so this list can only change when that happens -- and it
  // used to be read once, so findings the running scan resolved stayed off the page until somebody
  // reloaded it. Why the first answer counts too: see useScanChanges.
  const scanChanges = useScanChanges(overview.state === 'ready' ? overview.data.freshness : null)
  const resolved = useApi(() => getResolvedFindings(), [queued, scanChanges], undefined, true)

  // Once a queued assessment shows up in the overview, the button is replaced by a line saying
  // so. When that happens because of a press on this page, focus goes to that line -- the button
  // that had it no longer exists.
  const headerStatus = useRef<HTMLParagraphElement>(null)
  const scanMoving =
    overview.state === 'ready' && !canRescan(overview.data.freshness)
  useEffect(() => {
    if (queued > 0 && scanMoving) headerStatus.current?.focus()
  }, [queued, scanMoving])

  if (overview.state === 'loading') {
    return <div className="live-empty">Loading…</div>
  }

  if (overview.state === 'error') {
    return <div className="live-error">{describeFailure(overview.status)}</div>
  }

  // The chrome around this card is captioned with a workspace name the shell fetched earlier. If
  // the session has moved since -- a second tab signing into another workspace replaces the shared
  // cookie -- these numbers belong to a different tenant than the name above them. Refuse rather
  // than render the two together.
  if (!agrees(session, overview.data.workspaceId)) {
    return (
      <div className="live-error">
        {session.state === 'unknown' ? SESSION_UNKNOWN : SESSION_CHANGED}
      </div>
    )
  }

  const {
    freshness, activeBySeverity, activeByArea, suppressed, unreadControls, secureScore, assessedAreas,
  } = overview.data

  // Zero means "nothing found" only when a scan actually completed. Anywhere else it means
  // "nothing looked", and every zero on this page is worded differently because of it. The flag
  // comes from the server, which weighs the status against the area counts; deriving it here from
  // `status === 'complete'` is what let a scan covering three of five areas read as finished.
  const trustworthy = freshness.complete

  // Titles only. Where this read failed the cards still show the server's counts -- a count with
  // no titles is incomplete, a count that disappeared because a second request failed is a lie.
  const items = worst.state === 'ready' ? worst.data.items : []
  const titlesMissing = worst.state === 'error'

  const bySeverity = groupBy(items, (f) => f.severity)
  const total = Object.values(activeBySeverity).reduce((sum, n) => sum + n, 0)

  // Informational is not a rank, so it is not in the row of four. It appears as a fifth card only
  // when the tenant actually has some: a permanent empty card at the end of a ranked row invites
  // the row to be read as five levels, and a level that is always zero teaches people to ignore
  // the whole row.
  const informational = activeBySeverity.Informational ?? 0

  return (
    <>
      {/* ── header ────────────────────────────────────────────────────────────────────── */}
      <header className="dash-header">
        <div className="dash-header-who">
          {/*
            The workspace's own name, or the page's. Never a placeholder that could be read as the
            tenant's -- the shell takes the same position with the breadcrumb, and for the same
            reason: there is no safe stand-in for a customer's name on a page of their findings.

            The design also carries a verified domain beside this and a "next scheduled assessment"
            countdown. Neither is dropped by oversight: the session returns no tenant domain, and
            nothing in this product schedules a second assessment on the free plan, so both would
            be text invented to fill a shape.
          */}
          {/* h2: the shell's h1 names the page ("Dashboard"); a second h1 made two top-level
              headings on one page. */}
          <h2 className="dash-title">{personOf(session)?.workspaceName ?? 'Dashboard'}</h2>
        </div>

        {/*
          The design's primary action, in the design's position. Freshness below is told not to
          render its own copy -- see showRescan -- so there is exactly one control on this page
          that queues a scan, and it does not move about depending on how the last one ended.

          "Export report" is the design's other button and is deliberately not here: export lives on
          the Reports page (a live CSV, a printable report, and the sealed record when one exists),
          where the scope picker that shapes it also lives. Keeping it off the dashboard header leaves
          this page one primary control that does not move about depending on how the last scan ended.
        */}
        {/* The button, or -- while an assessment is queued or running -- a line saying so, in the
            same place. It used to be the button or nothing, so after a press the only trace was
            " · a new assessment is queued" in small muted text further down. */}
        <div className="dash-header-action">
          {canRescan(freshness) ? (
            <RescanButton onQueued={() => setQueued((n) => n + 1)} label="Assess now" />
          ) : (
            <p ref={headerStatus} tabIndex={-1} className="dash-header-status" role="status">
              {freshness.status === 'running'
                ? 'Assessment running — this page updates as each area finishes.'
                : 'Assessment queued — it starts as soon as the scanner is free, and this page updates when it does.'}
            </p>
          )}
        </div>
      </header>

      {/*
        Directly under the title, because it says how to read everything below it. On a healthy
        tenant this is the design's one-line subtitle; on a partial, failed or queued one it is a
        card that interrupts, which is the whole reason it is a component and not a line of text.
      */}
      <Freshness freshness={freshness} showRescan={false} />

      {/*
        Controls this scan tried to read and could not. Between the freshness banner and the
        headline panels because it is the same kind of fact -- how much of this page to trust -- and
        it names the specific gap the freshness line only implies. Renders nothing on a healthy scan.
      */}
      <UnreadControls
        controls={unreadControls}
        // The no-devices group links to the finding it waits on. Its live title when that finding is
        // on the worst-first page already read; the catalogue's otherwise.
        noDevicesFinding={
          items.find((f) => f.catalogKey === NO_DEVICES_FINDING.key)?.title ?? NO_DEVICES_FINDING.title
        }
      />

      {/*
        What this scan did not assess because the tenant is not licensed for it (0158). Beside the
        could-not-read card and never inside it: "a permission may have changed" about a service the
        tenant never bought was the false alarm this card replaces (owner, 2026-09-26). Read from the
        freshness copy of the list, the same one the area rows below and the freshness line above
        read, so the three cannot disagree.
      */}
      <NotLicensedCard groups={notLicensedOf(freshness)} />

      {/* ── the two headline panels ───────────────────────────────────────────────────────
          What we look at, and what we found. These stand where a competitor puts a score donut,
          and neither of them is a score: one is a ratio against a published denominator, the
          other is a count of named findings. */}
      <div className="dash-headline">
        <CoverageLedger />
        <OpenFindings
          total={total}
          activeByArea={activeByArea}
          assessedAreas={assessedAreas}
          freshness={freshness}
          worstByArea={worstSeverityByArea(items)}
          resolved={resolved}
          trustworthy={trustworthy}
        />
      </div>

      {/* ── Microsoft's own score ──────────────────────────────────────────────────────────
          Under the headline panels, not beside them: those two are our claims, and a gauge in
          the same row would read as the third. It was a finding under a "Cloud" area until
          2026-09-25; a score is not a failed control, so it is a reading now, shown as one. */}
      <SecureScoreGauge reading={secureScore} />

      {/* ── severity cards, each carrying its own findings ─────────────────────────────────
          A count with nothing under it makes a reader click to find out whether it matters.
          Three titles under the number answer that in place. */}
      <div className="sev-row">
        {RANKED.map((severity) => (
          <SeverityCard
            key={severity}
            severity={severity}
            count={activeBySeverity[severity] ?? 0}
            titles={bySeverity.get(severity) ?? []}
            trustworthy={trustworthy}
            neverScanned={freshness.status === null}
            titlesMissing={titlesMissing}
            onOpen={onOpen}
          />
        ))}

        {informational > 0 ? (
          <SeverityCard
            severity="Informational"
            count={informational}
            titles={bySeverity.get('Informational') ?? []}
            trustworthy={trustworthy}
            titlesMissing={titlesMissing}
            onOpen={onOpen}
          />
        ) : null}
      </div>

      {/* ── what to do first, and what has already been done ───────────────────────────── */}
      <div className="dash-lower">
        <FixTheseFirst
          rows={items.slice(0, FIX_FIRST_ROWS)}
          load={worst}
          trustworthy={trustworthy}
          activeTotal={total}
          suppressed={suppressed}
          notAssessed={notAssessedAreasSentence(freshness)}
          onOpen={onOpen}
        />
        <RecentlyResolved resolved={resolved} session={session} />
      </div>
    </>
  )
}

/* ── controls a scan could not read ───────────────────────────────────────────────────────── */

/**
 * Controls the most recent scan attempted and could not read, named and grouped by area — so a
 * customer sees WHICH control went dark, not just that coverage slipped. Renders nothing on a
 * healthy scan.
 *
 * Two kinds, in two cards, because they mean different things and call for different reactions:
 *   could_not_run -- a check that should have answered and did not (amber: something to look into);
 *   withheld (0152) -- a check waiting on a change in the tenant that is already its own finding
 *   (neutral: nothing failed; do that one first). Showing the second under the first's "a permission
 *   may have changed" is what made a healthy scan read as broken (owner, 2026-09-25).
 *
 * A licence gap (skipped) is steady and belongs to the coverage ledger, so it is deliberately absent
 * here rather than padding this list with policies the tenant was never going to be assessed for.
 * So is a control not assessed because Microsoft refused a service the tenant holds no licence for
 * (0158): the server never puts those in this list, and they have a card of their own below.
 */
export function UnreadControls({
  controls,
  noDevicesFinding,
}: {
  controls: UnreadControl[]
  noDevicesFinding: string
}) {
  const waiting = controls.filter(isWaiting)
  const unread = controls.filter((c) => !isWaiting(c))

  return (
    <>
      <CouldNotRead controls={unread} noDevicesFinding={noDevicesFinding} />
      <WaitingOnPrerequisite controls={waiting} />
    </>
  )
}

/**
 * The controls that produced no verdict, and WHY (0159).
 *
 * Each control now carries the reason the scan recorded for it, and the card says it per reason --
 * one section for each reason present, actionable first (unreadReasons.ts holds the words and the
 * order). One sentence for all of them was wrong three ways in one week: a Defender permission
 * Defenssive's access lacked, Defender with no devices to report on, and refusals and throttling
 * with causes of their own were all told "a permission may have changed, or Microsoft may have
 * been unavailable" (owner, 2026-09-26).
 *
 * A scan recorded before 0159 carries no reason, and its card is exactly the card it always was.
 */
function CouldNotRead({
  controls,
  noDevicesFinding,
}: {
  controls: UnreadControl[]
  noDevicesFinding: string
}) {
  if (controls.length === 0) return null

  const noun = controls.length === 1 ? 'control' : 'controls'
  const sections = sectionsOf(controls, noDevicesFinding)

  if (sections.length === 1 && sections[0]?.id === 'legacy') {
    return (
      <Card className="unread-controls">
        <h2 className="unread-controls-title">
          {controls.length} {noun} could not be read on your most recent scan
        </h2>
        <p className="unread-controls-body">
          These were attempted but did not return a usable result — a permission may have changed, or
          Microsoft may have been unavailable. They are <strong>not</strong> counted as passing; the
          next complete scan re-checks them.
        </p>
        <ControlsByDomain controls={controls} />
      </Card>
    )
  }

  const one = controls.length === 1

  // Neutral, like the waiting card, only when nothing on it went wrong: every section is the tenant
  // lacking something (devices reporting to Defender, a feature its plan does not include). A card
  // that also holds a refusal or an error keeps the amber of something to look into.
  const calm = sections.every((s) => s.calm)

  // The no-devices section says "not counted as passing" in its own words; a card that is only that
  // section does not say it twice.
  const onlyNoDevices = sections.length === 1 && sections[0]?.id === 'noDevices'

  return (
    <Card className={`unread-controls${calm ? ' is-calm' : ''}`}>
      {/* "Not assessed", never "could not be read": true of every reason on the card -- including a
          tenant whose Defender lists no devices, where the read worked and answered "none". */}
      <h2 className="unread-controls-title">
        {controls.length} {noun} {one ? 'was' : 'were'} not assessed on your most recent scan
      </h2>
      {onlyNoDevices ? null : (
        <p className="unread-controls-body">
          {one ? 'It is' : 'They are'} <strong>not</strong> counted as passing, and nothing{' '}
          {one ? 'it' : 'they'} found before has been closed.
          {sections.length > 1 ? ' Grouped below by reason.' : ''}
        </p>
      )}
      {sections.map((section) => (
        <ReasonSection key={section.id} section={section} />
      ))}
    </Card>
  )
}

/** One reason on the could-not-read card: what it is, how many, which controls, why, and what to do. */
function ReasonSection({ section }: { section: UnreadSection }) {
  const headingId = useId()
  const n = section.controls.length

  return (
    <section className={`unread-reason is-${section.id}`} aria-labelledby={headingId}>
      <h3 id={headingId} className="unread-reason-title">
        {section.heading}
        <span className="unread-reason-count">
          {' '}— {n} {n === 1 ? 'control' : 'controls'}
        </span>
      </h3>
      <p className="unread-controls-body unread-reason-body">{section.sentence}</p>
      {section.remedy ? (
        <p className="unread-reason-remedy">
          <Link to={section.remedy.to}>{section.remedy.label}</Link>
        </p>
      ) : null}
      <ControlsByDomain controls={section.controls} />
    </section>
  )
}

/** The controls, under their areas, as the card has always listed them. */
function ControlsByDomain({ controls }: { controls: UnreadControl[] }) {
  const byDomain = groupBy(controls, (c) => c.domain ?? 'Other')

  return (
    <>
      {[...byDomain.entries()].map(([domain, items]) => (
        <div key={domain} className="unread-controls-group">
          <div className="unread-controls-domain">{domain}</div>
          <ul className="unread-controls-list">
            {items.map((c) => (
              <li key={c.key} className="unread-controls-item">{c.title}</li>
            ))}
          </ul>
        </div>
      ))}
    </>
  )
}

function WaitingOnPrerequisite({ controls }: { controls: UnreadControl[] }) {
  if (controls.length === 0) return null

  const one = controls.length === 1

  return (
    <Card className="unread-controls is-waiting">
      <h2 className="unread-controls-title">
        {one ? '1 check is' : `${controls.length} checks are`} waiting on a change in your tenant
      </h2>
      <p className="unread-controls-body">
        {one ? 'This check gives' : 'These checks give'} a reliable answer only once the change named
        below is made, so {one ? 'it was' : 'they were'} not assessed this time. Nothing is wrong with
        the connection, {one ? 'it is' : 'they are'} <strong>not</strong> counted as passing, and
        nothing {one ? 'it' : 'they'} found before has been closed. The change itself is raised as its
        own finding.
      </p>
      <ul className="unread-controls-list">
        {controls.map((c) => {
          const waitsOn = c.waitsOn?.title
          return (
            <li key={c.key} className="unread-controls-item">
              {c.title}
              {waitsOn ? (
                <span className="unread-controls-waits">
                  Waits on:{' '}
                  <Link to={`/recommendations?q=${encodeURIComponent(waitsOn)}`}>{waitsOn}</Link>
                </span>
              ) : null}
            </li>
          )
        })}
      </ul>
    </Card>
  )
}

/**
 * What the most recent scan did not assess because the tenant is not licensed for it (0158, owner
 * 2026-09-26): read FIRST, and called not licensed only when Microsoft refused the read AND the
 * tenant's licences are absent for that service. Renders nothing on a tenant licensed for
 * everything the scan reads.
 *
 * Neutral, like the waiting card and for the same reason: nothing failed and nothing is wrong with
 * the connection. The tenant does not have the service, and the customer is told so in those words
 * rather than "a permission may have changed, or Microsoft may have been unavailable" -- which is
 * what a tenant licensed only for Entra ID P2 was told about 24 controls it had never bought.
 *
 * What the card must still say, because the calm colour must not read as good news: these are NOT
 * counted as passing, and nothing found in them before has been closed. Both are true on the
 * server -- a not-licensed control never runs, so it resolves nothing.
 */
export function NotLicensedCard({ groups }: { groups: NotLicensedControls[] }) {
  if (groups.length === 0) return null

  const one = serviceNames(groups).length <= 1

  return (
    <Card className="unread-controls is-not-licensed">
      <h2 className="unread-controls-title">Not assessed: not licensed</h2>
      <p className="unread-controls-body">
        Microsoft refused to let us read {one ? 'this service' : 'these services'}, and this tenant holds
        no licence for {one ? 'it' : 'them'}, so the controls that need {one ? 'it' : 'them'} were not
        assessed. They are <strong>not</strong> counted as passing, and nothing found in them before has
        been closed. If the tenant gets {one ? 'the licence' : 'a licence'}, the next assessment reads{' '}
        {one ? 'it' : 'that service'}.
      </p>
      <ul className="unread-controls-list">
        {groups.map((g) => (
          <li key={`${g.area}|${g.service}`} className="unread-controls-item">
            <strong>{g.area}</strong>: no {g.serviceName} licence ({controlsWord(g.controls)},{' '}
            {g.wholeArea ? `all of ${g.area}` : `part of ${g.area}`})
          </li>
        ))}
      </ul>
    </Card>
  )
}

/* ── open findings ──────────────────────────────────────────────────────────────────────── */

interface AreaRow {
  area: string
  count: number

  /** The scan finished this area, so a zero here is a real zero. */
  assessed: boolean

  /** The worst level seen in this area, or null when no title for it arrived. */
  worst: string | null

  /**
   * Nothing in this area was assessed, because the tenant is not licensed for the service it reads
   * (0158). The scan marks such an area complete, which is exactly why this has to be carried
   * separately: complete-with-nothing-open is otherwise the picture of a clean area.
   */
  notLicensed: boolean

  /**
   * The line under the row that says what was not assessed for want of a licence, or null. On a
   * wholly unlicensed area and on one assessed only in part (Endpoint's Defender controls). Also how
   * many of its checks saw only part of what they needed, where any did (partlyRead.ts).
   */
  note: string | null
}

/**
 * The line under an area row about what the latest scan did not assess for want of a licence.
 *
 * Three cases, because they mean three different things:
 *  - nothing assessed and nothing open: the whole answer is that the area was not assessed;
 *  - nothing assessed but findings open: those came from earlier assessments, and they stay open --
 *    a not-licensed control never runs, so it closes nothing (owner, 2026-09-26). A tenant that let
 *    a licence lapse must not read its old findings as current, nor wonder where they went;
 *  - some controls not assessed: how many, and which licence, so "Endpoint 0" is not read as a
 *    whole Endpoint found clean.
 */
function licenceNote(
  whole: boolean,
  groups: NotLicensedControls[],
  count: number,
): string | null {
  if (whole) {
    return count === 0
      ? `Not assessed: this tenant has ${noLicenceFor(groups)}.`
      : `Not assessed this time: this tenant has ${noLicenceFor(groups)}. `
        + 'Findings from earlier assessments stay open.'
  }
  if (groups.length === 0) return null
  return `${controlsWord(controlCount(groups))} not assessed: this tenant has ${noLicenceFor(groups)}.`
}

/**
 * An area row's accessible name: "Identity, 28 open findings", "Email, not assessed: this tenant has
 * no Exchange Online licence".
 *
 * The licence note is part of the name, not only a line under the row: a screen-reader user
 * hearing "Endpoint, 0 open findings" deserves the "5 controls not assessed" a sighted reader sees.
 */
function rowName(row: AreaRow): string {
  const note = row.note?.replace(/\.$/, '') ?? null

  if (row.count === 0 && row.notLicensed && note) {
    return `${row.area}, ${note.charAt(0).toLowerCase()}${note.slice(1)}`
  }

  const state = row.count === 0 && !row.assessed
    ? 'not assessed'
    : `${row.count} open ${row.count === 1 ? 'finding' : 'findings'}`

  return `${row.area}, ${state}${note ? `. ${note}` : ''}`
}

/**
 * What was found, by area.
 *
 * The counts are the server's. What this panel adds is the denominator the design asks for: an
 * area with no findings and an area nobody looked at are drawn differently and worded differently,
 * because they are the difference between "clean" and "unknown" — and a bar chart that renders
 * them identically is the exact shape of the mistake this product cannot make.
 */
function OpenFindings({
  total,
  activeByArea,
  assessedAreas,
  freshness,
  worstByArea,
  resolved,
  trustworthy,
}: {
  total: number
  activeByArea: Record<string, number>
  /** The areas this build assesses. Absent from an older server, which keeps the old behaviour. */
  assessedAreas: string[] | undefined
  freshness: ScanFreshness
  worstByArea: Map<string, string>
  resolved: Load<ResolvedFindings>
  trustworthy: boolean
}) {
  /*
    Every area either the findings or the scan knows about.

    activeByArea is a GROUP BY over active findings, so an area with nothing open is simply absent
    from it -- there is no zero to render. freshness.areas is the other half: the scan records a
    row per area it set out to cover, whatever it found. The union is what lets a zero be shown at
    all, and the scan's own status is what says whether that zero was earned.

    Then narrowed to the areas this build still assesses. A scan taken before an area was retired
    (Cloud, 2026-09-25) still carries a completed row for it, which drew "Cloud 0" -- a clean result
    for something nobody assesses any more. An area with OPEN findings is kept whatever the list
    says: hiding a count because its area was renamed would make the rows sum to less than the
    total above them, and it is the flattering direction.
  */
  const current = assessedAreas ? new Set(assessedAreas) : null
  const names = [...new Set([...Object.keys(activeByArea), ...freshness.areas.map((a) => a.area)])]
    .filter((area) => current === null || current.has(area) || (activeByArea[area] ?? 0) > 0)

  // Areas that finished while some of their checks saw only part of what they needed. Said under the
  // row, beside the count a low number would otherwise be read from; the freshness line above names
  // the checks and why (review, 2026-09-27).
  const partlyRead = new Map(partlyReadAreas(freshness, { finishedOnly: true }).map((p) => [p.area, p]))
  // And where that record is there but could not be read, said as that rather than left blank.
  const unconfirmed = new Set(unconfirmedAreas(freshness))

  const rows: AreaRow[] = names.map((area) => {
    const count = activeByArea[area] ?? 0
    const notLicensed = wholeAreaNotLicensed(freshness, area)
    const partly = partlyRead.get(area)
    const notes = [
      licenceNote(notLicensed, notLicensedIn(freshness, area), count),
      partly ? partlyReadNote(partly) : null,
      unconfirmed.has(area) ? 'Could not confirm that every check here read its evidence in full.' : null,
    ].filter((n): n is string => n !== null)
    return {
      area,
      count,
      // A finding proves the area ran, whatever the scan row says. Otherwise the scan's own word --
      // except that "complete" over an area nothing in which is licensed is not a scan that looked
      // (0158): that zero was never measured, and is drawn as such.
      assessed: count > 0
        || (!notLicensed && freshness.areas.some((a) => a.area === area && a.status === 'complete')),
      worst: worstByArea.get(area) ?? null,
      notLicensed,
      note: notes.length > 0 ? notes.join(' ') : null,
    }
  })

  rows.sort((a, b) => b.count - a.count || Number(b.assessed) - Number(a.assessed)
    || a.area.localeCompare(b.area))

  const largest = rows.reduce((most, row) => Math.max(most, row.count), 0)
  const withFindings = rows.filter((row) => row.count > 0).length

  // Only from an answer. A failed or absent read must not become "0 resolved", which would be a
  // claim about the tenant made out of a claim about our own server. A finding closed as not
  // applicable (0176), under the organisation profile or because of the tenant's licences, is listed
  // too, and counted as that, never as resolved.
  const listed = resolved.state === 'ready' ? resolved.data.items : null
  const noLongerApplyCount = listed
    ? listed.filter((finding) => finding.closedAs === 'not_applicable').length
    : null
  const resolvedCount = listed && noLongerApplyCount !== null ? listed.length - noLongerApplyCount : null

  return (
    <section className="dash-panel">
      <div className="dash-kicker">Open findings</div>

      <p className="dash-figure">
        <span className="dash-figure-value">{total}</span>
        <span className="dash-figure-unit">
          {/* "0 across 0 areas" is what counting areas-with-findings says when there are none,
              and it reads as a broken template rather than as a result. The rows below still
              give every area its own state, which is where a zero has to be explained anyway. */}
          {total === 0
            ? 'open findings'
            : withFindings === 1
              ? 'in 1 area'
              : `across ${withFindings} areas`}
        </span>
      </p>

      <p className="dash-lede">
        {/* Describes the LIST, not a total. The panel asks for a handful of the most recent, so
            "5 were resolved" would be a count of what fitted on screen presented as a count of
            what happened. */}
        {listedSentence(resolvedCount ?? 0, noLongerApplyCount ?? 0)}
        Nothing here was inferred — every finding names the control it failed and the evidence
        that failed it.
        {trustworthy ? '' : ' The assessment above did not complete, so this is a floor and not a total.'}
      </p>

      {rows.length === 0 ? (
        <p className="live-empty">
          No area has reported yet. <strong>This is not an all-clear</strong> — it means nothing has
          been checked.
        </p>
      ) : (
        <div className="dash-area-rows">
          {rows.map((row) => {
            // Each row opens that area's findings (owner, 2026-09-25). The whole row is the target,
            // named in words -- the bar and the number are pictures of the same fact.
            // Cloud has a page only where Azure is assessed (in this server's list); an older Cloud
            // finding elsewhere is drawn and not linked, since /cloud would only redirect.
            const page = (row.area === 'Cloud' || row.area === 'AI') && !current?.has(row.area) ? undefined : AREA_PAGES[row.area]
            const name = rowName(row)
            const content = (
              <>
              <span className="dash-area-name">{row.area}</span>

              <div className="dash-area-track">
                {row.count === 0 && row.notLicensed ? (
                  // Words, like "not assessed" below, and naming WHY: an area never read because
                  // the tenant does not hold the licence is a different gap from one a scan could
                  // not finish, and neither is a bar of length zero.
                  <span className="dash-area-unassessed">not licensed</span>
                ) : row.count > 0 ? (
                  <div
                    // The fill is coloured by the WORST level open in the area, not by how many
                    // there are: a red bar means this area holds a Critical or a High, which is a
                    // fact from the data. Sizing by count and colouring by count would say the
                    // same thing twice and neither time honestly.
                    className={`dash-area-fill ${toneClass(row.worst)}`}
                    style={{ width: `${largest > 0 ? Math.max(4, (row.count / largest) * 100) : 0}%` }}
                  />
                ) : row.assessed ? null : (
                  // NOT a bar of length zero. An area the scan could not finish has no measured
                  // value, and drawing it as an empty track beside an area that finished and
                  // found nothing would make "we did not look" and "there is nothing here" the
                  // same picture.
                  <span className="dash-area-unassessed">not assessed</span>
                )}
              </div>

              <span className={`dash-area-count${row.count === 0 ? ' is-zero' : ''}`}>
                {row.count === 0 && !row.assessed ? '—' : row.count}
              </span>

              {/* Under the row, full width: the service name does not fit in the track on a phone,
                  and a reason cut off at "no SharePoint an…" is not a reason. */}
              {row.note ? <span className="dash-area-note">{row.note}</span> : null}
              </>
            )

            const rowClass = `dash-area-row${row.note ? ' has-note' : ''}${row.notLicensed ? ' is-not-licensed' : ''}`

            return page ? (
              <Link className={`${rowClass} is-link`} key={row.area} to={page} aria-label={name}>
                {content}
              </Link>
            ) : (
              <div className={rowClass} key={row.area}>
                {content}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

/* ── severity cards ─────────────────────────────────────────────────────────────────────── */

function SeverityCard({
  severity,
  count,
  titles,
  trustworthy,
  titlesMissing,
  onOpen,
  neverScanned = false,
}: {
  severity: string
  count: number
  titles: FindingSummary[]
  trustworthy: boolean
  titlesMissing: boolean
  onOpen?: (finding: FindingSummary) => void
  /** No assessment has ever run. Distinct from an incomplete one, which DID check some things. */
  neverScanned?: boolean
}) {
  const shown = titles.slice(0, TITLES_PER_CARD)
  const more = count - shown.length

  return (
    <section className={`sev-card sev-card-${severity.toLowerCase()}`}>
      <div className="sev-card-head">
        <span className="sev-card-name">{severity}</span>
        {/* Dimmed at zero, and only at zero. A zero is not an achievement to be set in the same
            weight as a twenty-two, and on an incomplete scan it is not even a result. */}
        <span className={`sev-card-count${count === 0 ? ' is-zero' : ''}`}>{count}</span>
      </div>

      <div className="sev-card-body">
        {count === 0 ? (
          <span className="sev-card-empty">
            {/* "None active", not "None found": a finding of this severity may have been SET ASIDE,
                and this card counts only active ones. "Found" would claim it never existed. The
                set-aside total and its caveat live on the "Fix these first" panel. */}
            {/* Three answers, not two. "Nothing has been checked" was shown on PARTIAL and running
                scans too, which had checked most things -- it is only true when nothing ran. */}
            {neverScanned
              ? 'Nothing has been checked yet.'
              : trustworthy
                ? 'None active.'
                : 'None active — the latest assessment is incomplete, so this is not an all-clear.'}
          </span>
        ) : shown.length === 0 ? (
          <span className="sev-card-empty">
            {titlesMissing ? (
              'Titles could not be loaded.'
            ) : (
              <>Listed under <a href="/recommendations">Recommendations</a>.</>
            )}
          </span>
        ) : (
          shown.map((finding) => (
            <FindingTitle key={finding.id} finding={finding} onOpen={onOpen} />
          ))
        )}

        {/* A way through, not a dead end: the rest are on Recommendations. */}
        {more > 0 && shown.length > 0 ? (
          <a className="sev-card-more" href="/recommendations">+ {more} more</a>
        ) : null}
      </div>
    </section>
  )
}

/* ── fix these first ────────────────────────────────────────────────────────────────────── */

/**
 * The worst open findings, in the order the server ranked them.
 *
 * The design captions this "highest severity, lowest effort". This panel deliberately shows only
 * the first half. Effort IS available now — /api/findings returns it and an impact sort (severity
 * then effort) exists (FindingSort.Impact) — but on THIS panel a customer's short "do these now"
 * list is ordered by pure severity (most severe, most recently seen), because letting effort float a
 * quick Low above a Critical is the wrong call for a triage list even when the effort data is real.
 * So the caption states the ordering the page actually uses, and the effort column the design draws
 * is left unfilled rather than shown here. (The full impact ordering is offered on the Reports/
 * findings surfaces, where sorting by it is the user's choice.)
 */
function FixTheseFirst({
  rows,
  load,
  trustworthy,
  activeTotal,
  suppressed,
  notAssessed = '',
  onOpen,
}: {
  rows: FindingSummary[]
  load: Load<unknown>
  trustworthy: boolean
  activeTotal: number
  suppressed: number
  /**
   * The sentence naming areas not assessed at all because the tenant is not licensed for them
   * (0158), or empty. A complete scan with nothing open is otherwise this panel's all-clear, and
   * "Nothing needs fixing" over an Email area nobody could read is an all-clear for it too.
   */
  notAssessed?: string
  onOpen?: (finding: FindingSummary) => void
}) {
  return (
    <section className="dash-panel">
      <div className="dash-panel-head">
        <h2 className="dash-panel-title">Fix these first</h2>
        <span className="dash-panel-note">Highest severity, most recently seen</span>
      </div>

      {/* The all-clear CLAIM is made from the overview alone — activeTotal, suppressed and
          trustworthy all come from one /overview snapshot, so they cannot contradict each other.
          It is NOT made from `rows`, which is a separate fetch: basing "nothing needs fixing" on a
          findings list taken at a different instant than the suppressed count let a disposition
          landing between the two reads read as clean. `rows` only fills the table once we know
          there is something active to rank. */}
      {load.state === 'error' ? (
        <p className="live-error">{describeFailure(load.status)}</p>
      ) : activeTotal === 0 ? (
        <p className="live-empty">
          {!trustworthy
            ? 'Nothing to rank yet — the assessment above has not produced a complete set of findings.'
            : suppressed > 0
              ? `No active findings — ${suppressed} set aside. Open the Set-aside list to be sure none still needs action; this is not an all-clear.`
              : notAssessed
                ? `No open findings in the areas that were assessed. ${notAssessed}`
                : 'No open findings. Nothing needs fixing from this assessment.'}
        </p>
      ) : rows.length === 0 ? (
        <p className="live-empty">Loading…</p>
      ) : (
        <div className="fix-table">
          <div className="dash-kicker fix-head">Finding</div>
          <div className="dash-kicker fix-head">Severity</div>

          {rows.map((finding) => (
            <Fragment key={finding.id}>
              <div className="fix-cell fix-title">
                <FindingTitle finding={finding} onOpen={onOpen} />
                {/* What tells same-titled rows apart -- three vulnerability bands share a title. */}
                <FindingScope summary={finding.summary} />
              </div>
              <div className="fix-cell">{severityTag(finding.severity)}</div>
            </Fragment>
          ))}
        </div>
      )}
    </section>
  )
}

/**
 * "2 recently resolved findings and 1 closed as not applicable are listed below. " -- the list below,
 * described by what it holds. Empty when it holds nothing.
 */
function listedSentence(resolved: number, noLongerApply: number): string {
  if (resolved <= 0 && noLongerApply <= 0) return ''

  const fixed = resolved > 0
    ? `${resolved} recently resolved ${resolved === 1 ? 'finding' : 'findings'}`
    : null
  const closed = noLongerApply > 0
    ? fixed
      ? `${noLongerApply} closed as not applicable`
      : `${noLongerApply} ${noLongerApply === 1 ? 'finding' : 'findings'} recently closed as not applicable`
    : null

  const subject = [fixed, closed].filter((part): part is string => part !== null).join(' and ')
  return `${subject} ${resolved + noLongerApply === 1 ? 'is' : 'are'} listed below. `
}

/* ── recently resolved ──────────────────────────────────────────────────────────────────── */

/**
 * Work that landed.
 *
 * Two findings can go away and the product has nowhere to say so, which is the difference between
 * a tool that nags and one somebody wants to open. It was also the one panel here whose endpoint
 * did not exist when it was written, so it carries the case this codebase keeps making: an absent
 * answer and an empty one are not the same sentence, and the version that reads as good news is
 * never the one to default to.
 */
export function RecentlyResolved({
  resolved,
  session,
}: {
  resolved: Load<ResolvedFindings>
  session: SessionState
}) {
  // A finding closed as not applicable (0176) is closed, not fixed, so the heading and the sentence
  // under it must be true of it too whenever one is listed: worded as what the assessment recorded,
  // which stays true after the profile or the licences change. It names both causes -- the
  // workspace's organisation profile, and the tenant's licences (owner, 2026-09-29) -- because the
  // panel lists rows of either, and each finding's page gives its own reason. With none listed, the
  // panel says what it always said.
  const noLongerApplying = resolved.state === 'ready'
    && resolved.data.items.some((finding) => finding.closedAs === 'not_applicable')

  return (
    <section className="dash-panel">
      <h2 className="dash-panel-title">{noLongerApplying ? 'Recently closed' : 'Recently resolved'}</h2>
      <p className="dash-lede">
        {noLongerApplying
          ? 'Closed by a completed assessment, not by anyone ticking a box. A finding marked “Closed as ' +
            'not applicable” was closed because the assessment that closed it recorded its control as not ' +
            'applicable, under the workspace’s organisation profile or because of the tenant’s licences; ' +
            'it is not counted as fixed.'
          : 'Confirmed gone by a completed assessment, not by anyone ticking a box.'}
      </p>

      {/* No "Assessment history" block under the list. One used to promise a trend "once there is
          enough history", on tenants with dozens of assessments and in a product with no trend to
          show -- a sentence that could never come true. */}
      <div className="resolved-list">
        <ResolvedList resolved={resolved} session={session} />
      </div>
    </section>
  )
}

function ResolvedList({
  resolved,
  session,
}: {
  resolved: Load<ResolvedFindings>
  session: SessionState
}) {
  if (resolved.state === 'loading') return <p className="live-empty">Loading…</p>

  if (resolved.state === 'error') {
    // A 404 here is OUR route being absent, not the tenant having nothing. The headline stays the
    // same because it is true either way, and the line under it is what stops an unanswered
    // question being read as an answer -- the same distinction the rest of this page draws
    // between a zero that was measured and a zero that was never looked for.
    return resolved.status === 404 ? (
      <p className="resolved-empty">
        Nothing resolved yet.
        <span className="resolved-empty-why">
          We could not read what has been resolved, so an empty list here says nothing about your
          tenant either way.
        </span>
      </p>
    ) : (
      <p className="live-error">{describeFailure(resolved.status)}</p>
    )
  }

  // The panels beside this are captioned with a workspace name; these titles are a tenant's own.
  // Same check the overview makes, for the same reason.
  if (!agrees(session, resolved.data.workspaceId)) {
    return (
      <p className="live-error">
        {session.state === 'unknown' ? SESSION_UNKNOWN : SESSION_CHANGED}
      </p>
    )
  }

  if (resolved.data.items.length === 0) {
    return (
      <p className="resolved-empty">
        Nothing resolved yet.
        <span className="resolved-empty-why">
          A finding appears here once an assessment has run and stopped seeing it.
        </span>
      </p>
    )
  }

  return (
    <>
      {resolved.data.items.map((finding) => {
        // Absent on a server built before resolved_at was carried through, and null on anything
        // still open. The finding is still worth listing without it; a date printed from nothing
        // would be the one part of this panel a customer could catch us being wrong about.
        const when = finding.resolvedAt ? formatDay(finding.resolvedAt) : null

        // Closed as not applicable (0176), under the organisation profile or because of the tenant's
        // licences: said as that, and without the check mark, which is the panel's picture of "fixed".
        if (finding.closedAs === 'not_applicable') {
          return (
            <div className="resolved-item resolved-item-not-applicable" key={finding.id}>
              <span className="resolved-mark-placeholder" aria-hidden="true" />
              <div>
                <div className="resolved-title">{finding.title}</div>
                <div className="resolved-when">
                  Closed as not applicable{when ? ` · ${when}` : ''}
                </div>
              </div>
            </div>
          )
        }

        return (
          <div className="resolved-item" key={finding.id}>
            <CheckMark />
            <div>
              <div className="resolved-title">{finding.title}</div>
              {when ? <div className="resolved-when">Resolved {when}</div> : null}
            </div>
          </div>
        )
      })}
    </>
  )
}

function CheckMark() {
  return (
    <svg
      className="resolved-check"
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  )
}

/* ── shared ─────────────────────────────────────────────────────────────────────────────── */

/**
 * A finding's title, as a control when there is somewhere to go.
 *
 * Plain text otherwise rather than a link that does nothing — the same rule the header applies to
 * "Export report".
 */
export function FindingTitle({
  finding,
  onOpen,
}: {
  finding: FindingSummary
  onOpen?: (finding: FindingSummary) => void
}) {
  if (!onOpen) return <span className="finding-link-static">{finding.title}</span>

  return (
    <button type="button" className="finding-link" onClick={() => onOpen(finding)}>
      {finding.title}
    </button>
  )
}

/** Severity as the shared Tag understands it. Informational has no tone of its own. */
export function severityTag(severity: string) {
  const known: Severity[] = ['Critical', 'High', 'Medium', 'Low']
  return known.includes(severity as Severity) ? (
    <Tag severity={severity as Severity} />
  ) : (
    <Tag tone="outline">{severity}</Tag>
  )
}

/**
 * How many times, since this page opened, the overview has answered for the first time or named a
 * different latest scan or the same scan in a different state.
 *
 * Keyed on the scan's id AND its status, not on "running or not": a scan that closes and a queued
 * one that starts between two polls reads running then running, and only the id says the first one
 * finished and may have resolved findings. A failed read (null) changes nothing.
 *
 * The first answer counts as a change. The reads keyed on this go out beside the overview's first
 * read, not after it, and each is answered from its own snapshot: a scan that closes between the
 * two leaves a list from before the close under an overview that already says the scan finished.
 * A finished scan is not polled, so no later answer would ever differ from that first one, and the
 * list would stay without the findings the scan resolved until a reload. Reading once more when
 * the first answer lands costs one small read per page open and puts that read after a snapshot
 * that includes whatever the key says; after that, only a real change is counted.
 */
export function useScanChanges(freshness: ScanFreshness | null): number {
  const key = freshness ? `${freshness.scanId ?? ''}:${freshness.status ?? ''}` : null
  const seen = useRef<string | null>(null)
  const [changes, setChanges] = useState(0)

  useEffect(() => {
    if (key === null) return
    if (seen.current !== key) setChanges((n) => n + 1)
    seen.current = key
  }, [key])

  return changes
}

function groupBy<T>(items: T[], key: (item: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>()

  for (const item of items) {
    const group = groups.get(key(item))
    if (group) group.push(item)
    else groups.set(key(item), [item])
  }

  return groups
}

/**
 * The worst severity open in each area.
 *
 * Reads the worst-first page and keeps the FIRST occurrence of each area, which is that area's
 * worst by construction. An area whose findings all fall past the end of that page is absent, and
 * the bar for it is drawn in the neutral tone rather than guessed at — being absent from the top
 * hundred is not evidence of anything except that the page ended.
 */
function worstSeverityByArea(items: FindingSummary[]): Map<string, string> {
  const worst = new Map<string, string>()

  for (const finding of items) {
    if (!worst.has(finding.area)) worst.set(finding.area, finding.severity)
  }

  return worst
}

/** The severity ramp, as a class. Unknown and absent both land on the neutral tone. */
function toneClass(severity: string | null): string {
  const known = ['Critical', 'High', 'Medium', 'Low']
  return severity && known.includes(severity) ? `is-${severity.toLowerCase()}` : 'is-unranked'
}

/**
 * A date without a time.
 *
 * Absolute, like every other instant in this product — a relative "3 days ago" is read once and
 * remembered as if it stays true. The time of day is dropped here and only here: what matters
 * about a resolved finding is the day it went, and the assessment's own instant is on the
 * freshness line above.
 */
export function formatDay(value: string): string {
  const when = new Date(value)
  return Number.isNaN(when.getTime()) ? value : when.toLocaleDateString(undefined, { dateStyle: 'long' })
}
